/**
 * Base Socket Adapter
 *
 * Abstract base class implementing common functionality for socket adapters.
 * Provides event emitter pattern, connection state management, and utility methods.
 *
 * Part of Phase 2: Shared Interface & Adapter Pattern
 */

import type { Socket } from 'socket.io-client';
import type {
  ISocketAdapter,
  SocketMode,
  AdapterCapabilities,
  ConnectionConfig,
  ConnectionState,
  SessionOptions,
  SocketEvents,
  NormalizedSession,
  NormalizedMessage,
  StreamChunk,
  PairingCode,
  AuthTokens,
  DaemonStatus,
} from '../types/socketAdapter';
import type { MessageAttachment } from '../types/happy';
import {
  releaseConnection,
} from '../services/socketConnectionMutex';
import {
  clearJoinedSessions,
} from '../utils/eventIdempotency';
import {
  normalizeMessage,
  normalizeStreamChunk,
  normalizeSession,
  normalizeHistory,
} from '../utils/eventNormalizer';
import { shouldLogNormalizedEvents } from '../config/featureFlags';

// ============================================
// Event Emitter Implementation
// ============================================

type EventHandler = (...args: unknown[]) => void;
type EventHandlerMap = Map<string, Set<EventHandler>>;

/**
 * Abstract base class for socket adapters.
 * Implements common functionality while leaving mode-specific logic to subclasses.
 */
export abstract class BaseSocketAdapter implements ISocketAdapter {
  // ============================================
  // Abstract Properties (must be implemented)
  // ============================================

  abstract readonly mode: SocketMode;
  abstract readonly capabilities: AdapterCapabilities;

  // ============================================
  // Protected State
  // ============================================

  protected connectionState: ConnectionState = 'disconnected';
  protected eventHandlers: EventHandlerMap = new Map();
  protected activeSessions: Map<string, NormalizedSession> = new Map();
  protected messageCache: Map<string, NormalizedMessage[]> = new Map();

  /**
   * Socket instance - managed by child adapters
   * Protected to allow child adapters to access and setup event listeners
   */
  protected socket: Socket | null = null;

  /**
   * Socket ID for connection mutex tracking
   */
  protected socketId: string | null = null;

  // ============================================
  // Abstract Methods (must be implemented)
  // ============================================

  abstract connect(config: ConnectionConfig): Promise<boolean>;
  abstract disconnect(): void;
  abstract createSession(options?: SessionOptions): Promise<string | null>;
  abstract joinSession(sessionId: string): void;
  abstract leaveSession(sessionId: string): void;
  abstract terminateSession(sessionId: string): void;
  abstract sendMessage(sessionId: string, content: string, attachments?: MessageAttachment[]): boolean;

  // ============================================
  // Connection State
  // ============================================

  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  protected setConnectionState(state: ConnectionState): void {
    const previous = this.connectionState;
    this.connectionState = state;

    if (previous !== state) {
      this.emit('connection:state', state);
      console.log(`[${this.mode}Adapter] Connection state: ${previous} -> ${state}`);
    }
  }

  // ============================================
  // Event Emitter Pattern
  // ============================================

  on<K extends keyof SocketEvents>(event: K, handler: SocketEvents[K]): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler as EventHandler);
  }

  off<K extends keyof SocketEvents>(event: K, handler: SocketEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler as EventHandler);
    }
  }

  emit<K extends keyof SocketEvents>(
    event: K,
    ...args: Parameters<SocketEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(...args);
        } catch (error) {
          console.error(`[${this.mode}Adapter] Error in event handler for ${event}:`, error);
        }
      });
    }

    // Debug logging
    if (shouldLogNormalizedEvents()) {
      console.log(`[${this.mode}Adapter] Emitted ${event}:`, args);
    }
  }

  /**
   * Remove all handlers for an event (or all events if no event specified)
   */
  protected removeAllListeners(event?: keyof SocketEvents): void {
    if (event) {
      this.eventHandlers.delete(event);
    } else {
      this.eventHandlers.clear();
    }
  }

  // ============================================
  // Session Management Helpers
  // ============================================

  protected addSession(session: NormalizedSession): void {
    this.activeSessions.set(session.sessionId, session);
    this.emit('session:created', session);
  }

  protected updateSession(
    sessionId: string,
    updates: Partial<NormalizedSession>
  ): void {
    const existing = this.activeSessions.get(sessionId);
    if (existing) {
      const updated = { ...existing, ...updates };
      this.activeSessions.set(sessionId, updated);
      this.emit('session:updated', sessionId, updates);
    }
  }

  protected removeSession(sessionId: string, reason?: string): void {
    this.activeSessions.delete(sessionId);
    this.messageCache.delete(sessionId);
    this.emit('session:terminated', sessionId, reason);
  }

  protected getSession(sessionId: string): NormalizedSession | undefined {
    return this.activeSessions.get(sessionId);
  }

  protected getAllSessions(): NormalizedSession[] {
    return Array.from(this.activeSessions.values());
  }

  // ============================================
  // Message Management Helpers
  // ============================================

  protected addMessageToCache(message: NormalizedMessage): void {
    const messages = this.messageCache.get(message.sessionId) || [];

    // Check for duplicate
    if (messages.some((m) => m.messageId === message.messageId)) {
      console.log(`[${this.mode}Adapter] Skipping duplicate message:`, message.messageId);
      return;
    }

    messages.push(message);
    this.messageCache.set(message.sessionId, messages);
    this.emit('message:new', message);
  }

  protected getMessagesForSession(sessionId: string): NormalizedMessage[] {
    return this.messageCache.get(sessionId) || [];
  }

  protected clearMessagesForSession(sessionId: string): void {
    this.messageCache.delete(sessionId);
  }

  // ============================================
  // Event Normalization Helpers
  // ============================================

  /**
   * Process raw message:new event and emit normalized version
   */
  protected handleRawMessageNew(rawData: unknown): void {
    const normalized = normalizeMessage(rawData as Parameters<typeof normalizeMessage>[0]);
    if (normalized) {
      this.addMessageToCache(normalized);
    }
  }

  /**
   * Process raw message:stream event and emit normalized version
   */
  protected handleRawStreamChunk(rawData: unknown): void {
    const normalized = normalizeStreamChunk(rawData as Parameters<typeof normalizeStreamChunk>[0]);
    if (normalized) {
      this.emit('message:stream', normalized);
    }
  }

  /**
   * Process raw session event and emit normalized version
   */
  protected handleRawSession(rawData: unknown): void {
    const normalized = normalizeSession(rawData as Parameters<typeof normalizeSession>[0]);
    if (normalized) {
      this.addSession(normalized);
    }
  }

  /**
   * Process raw messages:history event and emit normalized version
   */
  protected handleRawHistory(rawData: unknown): void {
    const normalized = normalizeHistory(rawData as Parameters<typeof normalizeHistory>[0]);
    if (normalized) {
      // Cache messages
      this.messageCache.set(normalized.sessionId, normalized.messages);
      this.emit('messages:history', normalized.sessionId, normalized.messages);
    }
  }

  // ============================================
  // Cleanup
  // ============================================

  /**
   * Clean up all state on disconnect
   */
  protected cleanup(): void {
    this.activeSessions.clear();
    this.messageCache.clear();
    this.setConnectionState('disconnected');
  }

  // ============================================
  // Common Event Listeners (P3-T1: Hook Extraction)
  // ============================================

  /**
   * Setup common event listeners shared by all socket adapters.
   * Child adapters should call this method in their setupEventListeners()
   * and then add mode-specific handlers.
   *
   * Handles:
   * - disconnect: Connection state management, mutex release, session cleanup
   * - reconnect_attempt: Connection state update
   * - reconnect: Connection state update
   * - error: Error emission
   *
   * @param callbacks - Optional callbacks for mode-specific behavior during common events
   */
  protected setupCommonEventListeners(callbacks?: {
    onDisconnect?: (reason: string) => void;
    onReconnect?: () => void;
  }): void {
    if (!this.socket) {
      console.warn(`[${this.mode}Adapter] Cannot setup listeners - no socket`);
      return;
    }

    // Disconnect handler
    this.socket.on('disconnect', (reason: string) => {
      console.log(`[${this.mode}Adapter] Disconnected:`, reason);
      this.setConnectionState('disconnected');

      // Release connection mutex
      if (this.socketId) {
        releaseConnection(this.socketId);
      }

      // Clear joined sessions
      clearJoinedSessions();

      // Clear local state
      this.activeSessions.clear();
      this.messageCache.clear();

      // Call mode-specific disconnect handler if provided
      callbacks?.onDisconnect?.(reason);
    });

    // Reconnect attempt handler
    this.socket.on('reconnect_attempt', (attempt: number) => {
      console.log(`[${this.mode}Adapter] Reconnect attempt:`, attempt);
      this.setConnectionState('reconnecting');
    });

    // Reconnect handler
    this.socket.on('reconnect', () => {
      console.log(`[${this.mode}Adapter] Reconnected`);
      this.setConnectionState('connected');

      // Call mode-specific reconnect handler if provided
      callbacks?.onReconnect?.();
    });

    // Generic error handler (for socket.io errors not related to connection)
    this.socket.on('error', (error: Error) => {
      console.error(`[${this.mode}Adapter] Socket error:`, error.message);
      this.emit('connection:error', error.message);
    });

    console.log(`[${this.mode}Adapter] Common event listeners setup complete`);
  }

  // ============================================
  // Optional Methods (default implementations)
  // ============================================

  requestPairingCode?(): Promise<PairingCode | null> {
    console.warn(`[${this.mode}Adapter] requestPairingCode not supported`);
    return Promise.resolve(null);
  }

  confirmPairing?(code: string): Promise<AuthTokens | null> {
    console.warn(`[${this.mode}Adapter] confirmPairing not supported`);
    return Promise.resolve(null);
  }

  stopDaemon?(): Promise<boolean> {
    console.warn(`[${this.mode}Adapter] stopDaemon not supported`);
    return Promise.resolve(false);
  }

  restartSession?(sessionId: string): Promise<string | null> {
    console.warn(`[${this.mode}Adapter] restartSession not supported`);
    return Promise.resolve(null);
  }

  /**
   * Terminate all sessions (parent-only operation)
   */
  terminateAllSessions?(excludeParent?: boolean): Promise<{
    success: boolean;
    terminatedCount: number;
    terminatedSessions: string[];
    failedSessions?: string[];
    error?: string;
  }> {
    console.warn(`[${this.mode}Adapter] terminateAllSessions not supported`);
    return Promise.resolve({
      success: false,
      terminatedCount: 0,
      terminatedSessions: [],
      error: 'Not supported',
    });
  }

  /**
   * Restart all sessions (parent-only operation)
   */
  restartAllSessions?(excludeParent?: boolean): Promise<{
    success: boolean;
    restartedCount: number;
    sessionMapping: Record<string, string>;
    failedSessions?: string[];
    error?: string;
  }> {
    console.warn(`[${this.mode}Adapter] restartAllSessions not supported`);
    return Promise.resolve({
      success: false,
      restartedCount: 0,
      sessionMapping: {},
      error: 'Not supported',
    });
  }

  getDaemonStatus?(): DaemonStatus {
    return 'unknown';
  }

  spawnSession?(
    directory: string,
    machineId?: string
  ): Promise<{ ok: boolean; sessionId?: string; error?: string }> {
    console.warn(`[${this.mode}Adapter] spawnSession not supported`);
    return Promise.resolve({ ok: false, error: 'Not supported' });
  }

  archiveSession?(
    sessionId: string
  ): Promise<{ success: boolean; error?: string }> {
    console.warn(`[${this.mode}Adapter] archiveSession not supported`);
    return Promise.resolve({ success: false, error: 'Not supported' });
  }

  runCommand?(command: string, sessionId?: string): void {
    console.warn(`[${this.mode}Adapter] runCommand not supported`);
  }

  stopCommand?(): void {
    console.warn(`[${this.mode}Adapter] stopCommand not supported`);
  }
}
